// Hook for communicating with our r24 API
import { useLogger } from './Entities';
import { z } from 'zod';
import { useSelector } from '~Hooks';
import { AuthToken } from '~Types';

const BASE_URL = process.env.RETAIN24_ENDPOINT!;

const CardResponse = z.object({
  success: z.boolean(),
  balance: z.number(),
  currency: z.string(),
});

const PutResponse = z.object({
  success: z.boolean(),
});

const apiCall = async <Response>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  authToken: AuthToken,
  responseSchema: z.ZodType<Response>,
  data?: any,
) => {
  try {
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...(data !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });

    const responseData = await response.json().catch(() => undefined);

    if (!response.ok) {
      throw new Error(responseData?.message);
    }

    // Parse the response with zod
    const parsedResponse = responseSchema.parse(responseData);
    return parsedResponse;
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }

    // @ts-ignore
    throw new Error(`Failed to make API call: ${error.message}`);
  }
};

const useRetain24 = () => {
  const authToken = useSelector((state) => state.authToken);
  const logger = useLogger('retain24-api');

  const fetchBalance = async (code: string, venueId: string) => {
    logger.info('Fetching balance for card', { code, venueId });
    try {
      const response = await apiCall<z.infer<typeof CardResponse>>(
        `${BASE_URL}/cards/${code}?venueId=${venueId}`,
        'GET',
        authToken,
        CardResponse,
      );

      console.log(response);
      if (!response.success) {
        throw new Error('Failed to fetch balance');
      }

      return response;
    } catch (error) {
      logger.error('Failed to fetch balance', { errMessage: error });
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch balance');
    }
  };

  const makePurchase = async ({
    code,
    amount,
    venueId,
  }: {
    code: string;
    amount: number;
    venueId: string;
  }) => {
    logger.info('Making purchase with card', { code, amount, venueId });
    try {
      const response = await apiCall<z.infer<typeof PutResponse>>(
        `${BASE_URL}/cards/${code}`,
        'PUT',
        authToken,
        PutResponse,
        {
          paymentAmount: amount,
          venueId,
        },
      );

      if (!response.success) {
        throw new Error('Failed to make purchase');
      }

      return response;
    } catch (error) {
      logger.error('Failed to make purchase', { errMessage: error });
      throw new Error('Failed to make purchase');
    }
  };

  const refundPurchase = async ({
    code,
    amount,
    venueId,
  }: {
    code: string;
    amount: number;
    venueId: string;
  }) => {
    logger.info('Refunding purchase with card', { code, amount, venueId });
    try {
      const response = await apiCall<z.infer<typeof PutResponse>>(
        `${BASE_URL}/cards/${code}`,
        'PUT',
        authToken,
        PutResponse,
        {
          refundAmount: amount,
          venueId,
        },
      );

      if (!response.success) {
        throw new Error('Failed to refund purchase');
      }

      return response;
    } catch (error) {
      logger.error('Failed to refund purchase', { errMessage: error });
      throw new Error('Failed to refund purchase');
    }
  };

  const loadBalance = async ({
    code,
    amount,
    venueId,
  }: {
    code: string;
    amount: number;
    venueId: string;
  }) => {
    logger.info('Load card', { code, amount, venueId });
    try {
      const response = await apiCall<z.infer<typeof PutResponse>>(
        `${BASE_URL}/cards/${code}`,
        'PUT',
        authToken,
        PutResponse,
        {
          loadAmount: amount,
          venueId,
        },
      );

      if (!response.success) {
        throw new Error('Failed to load card');
      }

      return response;
    } catch (error) {
      logger.error('Failed to load card', { errMessage: error });
      throw new Error('Failed to load card');
    }
  };

  return {
    fetchBalance,
    makePurchase,
    refundPurchase,
    loadBalance,
  };
};

export default useRetain24;
